import { IVec3, Vec3 } from "@base/math/Vec3";
import { ImmHelper } from "@base/util/Immutable";
import { LoungeData } from "@biz/definitions/LoungeData";
import { ILoungeData, ILoungeItem, ILoungeReloadData, ILoungeReloadItem, IReloadChairSide, LoungeItemType, ReloadArmRestType, ReloadMaterial } from "@biz/definitions/LoungeDefinitions";
import { IClothData } from "@biz/logic/ClothData";
import { getItemLayerReducers } from "@biz/reducers/ItemLayerReducer";
import { getProduct3dReducers } from "@biz/reducers/Product3dReducer";

export const LoungeReducer = {
  ...getItemLayerReducers<ILoungeData>(),
  ...getProduct3dReducers<ILoungeData>(),
  setLoungeItemType(old: ILoungeData, itemIndex: number, loungeItemType: LoungeItemType): ILoungeData {
    return updateItems(old, (items) => {
      const newItem = ReloadChairReducer.setChairType(items[itemIndex], loungeItemType);
      const ret = ImmHelper.setArrayItem(items, itemIndex, newItem)
      return ret;
    });
  },
  setClothData(old: ILoungeData, cloth: IClothData | undefined, colorId: string | undefined, itemIndex: number | undefined): ILoungeData {
    if (itemIndex === undefined) {
      old = ImmHelper.setProperty(old, "global", "cloth", cloth);
      old = ImmHelper.setProperty(old, "global", "colorId", colorId || "");
      return old;
    } else {
      return updateItems(old, (items) => ImmHelper.setArrayItem(items, itemIndex, ReloadChairReducer.setClothColor(items[itemIndex], cloth, colorId)));
    }
  },
  setColorId(old: ILoungeData, colorId: string | undefined, itemIndex?: number): ILoungeData {
    if (itemIndex === undefined) {
      return ImmHelper.setProperty(old, "global", "colorId", colorId || "");
    }
    return updateItems(old, (items) => ImmHelper.setArrayItem(items, itemIndex, ReloadChairReducer.setColorId(items[itemIndex], colorId)));
  },
  setTransform(old: ILoungeData, params: { position: IVec3, rotationY: number }): ILoungeData {
    if (params.position !== undefined)
      old = ImmHelper.setProperty(old, "position", params?.position)
    if (params.position !== undefined)
      old = ImmHelper.setProperty(old, "rotationY", params?.rotationY)
    return old;
  },
  setPosition(old: ILoungeData, position: IVec3): ILoungeData {
    return ImmHelper.setProperty(old, "position", position)
  },
  setRotation(old: ILoungeData, rotation: number): ILoungeData {
    return ImmHelper.setProperty(old, "rotationY", rotation)
  },
  setNiveau(old: ILoungeData, niveau: number): ILoungeData {
    return ImmHelper.setProperty(old, "position", Vec3.withY(old.position, niveau))
  },
  addItem(old: ILoungeData, index: number, item: ILoungeItem): ILoungeData {
    return updateItems(old, (items) => ImmHelper.insertArrayItem(items, index, item) as any)
  },
  removeItem(old: ILoungeData, index: number): ILoungeData {
    const count = LoungeData.getItems(old).length;
    if (index >= count || count === 1)
      return old;
    return updateItems(old, (items) => ImmHelper.removeArrayItem(items as any[], index))
  }
}

export const LoungeReloadReducer = {
  setMaterial(old: ILoungeReloadData, material: ReloadMaterial | undefined, itemIndex?: number): ILoungeReloadData {
    if (itemIndex === undefined) {
      return ImmHelper.setProperty(old, "global", "material", material || "zinc");
    }
    return updateItems(old, (items) => ImmHelper.setArrayItem(items, itemIndex, ReloadChairReducer.setMaterial(items[itemIndex], material)));
  },
  setArmRest(old: ILoungeReloadData, _itemIndex: number, side: IReloadChairSide, value: ReloadArmRestType | undefined): ILoungeReloadData {
    return ImmHelper.setProperty(old, "armRest", ImmHelper.setProperty(old.armRest || {}, side, value));
  },
  setPillow(old: ILoungeReloadData, itemIndex: number, value: boolean): ILoungeReloadData {
    return updateItems(old, (items) => {
      items = ImmHelper.setArrayItem(items, itemIndex, ReloadChairReducer.setPillow(items[itemIndex], value));
      return items;
    });
  },
  setSidePillow(old: ILoungeReloadData, _itemIndex: number, side: keyof NonNullable<ILoungeReloadItem["sidePillow"]>, value: boolean): ILoungeReloadData {
    return ImmHelper.setProperty(old, "sidePillow", ImmHelper.setProperty(old.sidePillow || {}, side, value));
  },
}

function updateChain<T extends ILoungeData>(old: T, fn: (chain: T["chains"][0]) => T["chains"][0]): T {
  const newChain = fn(old.chains[0])
  return ImmHelper.setArrayItem(old, "chains", 0, newChain);
}

function updateItems<T extends ILoungeData>(old: T, fn: (items: T["chains"][0]["items"]) => T["chains"][0]["items"]): T {
  return updateChain(old, (chain) => ImmHelper.setProperty(chain, "items", fn(chain.items)));
}

export const ReloadChairReducer = {
  setChairType<T extends ILoungeItem>(old: T, itemType: LoungeItemType): T {
    return ImmHelper.setProperty(old, "itemType", itemType)
  },
  setClothColor(old: ILoungeItem, cloth: IClothData | undefined, colorId: string | undefined): ILoungeItem {
    old = ImmHelper.setProperty(old, "cloth", cloth)
    old = ImmHelper.setProperty(old, "colorId", colorId)
    return old;
  },
  setColorId(old: ILoungeItem, colorId: string | undefined): ILoungeItem {
    return ImmHelper.setProperty(old, "colorId", colorId)
  },
  setMaterial(old: ILoungeReloadItem, material: ReloadMaterial | undefined): ILoungeReloadItem {
    return ImmHelper.setProperty(old, "material", material)
  },
  // setArmRest(old: IReloadChair, side: IReloadChairSide, value: ReloadArmRestType | undefined): IReloadChair {
  //   if (!old.armRest)
  //     old = ImmHelper.setProperty(old, "armRest", {});
  //   return ImmHelper.setPropertyNonNullable(old, "armRest", side, value)
  // },
  setPillow(old: ILoungeReloadItem, flag: boolean): ILoungeReloadItem {
    return ImmHelper.setPropertyNonNullable(old, "pillow", flag)
  },
  // setSidePillow(old: IReloadChair, side: IReloadChairSide, flag: boolean): IReloadChair {
  //   if (!old.sidePillow)
  //     old = ImmHelper.setProperty(old, "sidePillow", {});
  //   return ImmHelper.setPropertyNonNullable(old, "sidePillow", side, flag)
  // }
}